import {assert} from 'Asserts';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {Batch, StorableSummary} from 'constants/SampleConstants';
import {SequenceSummary} from 'constants/SequenceConstants';
import * as SearchDAO from 'dao/SearchDAO';
import {BioentitySummary, isSchemaTag, SchemaTag, Tag} from 'folder-items/FileConstants';
import {
  ColumnDefinition,
  getColumnDefinitionIndex,
  getColumnDefinitionIndexForSample,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  LastSyncedUpstreamValue,
  SchemaFieldColumnUpdate,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import updateCellWithLiveValue from 'notebook/structured-tables/util/updateCellWithLiveValue';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Cells} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint, TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {TableData} from 'notebook/tables/constants/TableConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  LookupSpecifier,
  ServerLookupAggregatedQuery,
} from 'notebook/tables/lookup-field/LookupFieldDefinitionConstants';
import {
  getServerLookupQueries,
  mergeServerLookupResultsIntoCells,
} from 'notebook/tables/lookup-field/LookupFieldUtil';
import * as RefreshTableDataDAO from 'notebook/tables/refresh-table-data/RefreshTableDataDAO';
import TableUtil from 'notebook/tables/TableUtil';
import {
  convertTagToTypedValue,
  getBatchInfoFromTypedValue,
  getBioentityInfoFromTypedValue,
  getStorableInfoFromTypedValue,
  getTypedValueFromBioentitySummary,
  getTypedValueFromStorableSummaries,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import notifications from 'notifications';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import * as RegistryIdentifierUtilRest from 'registry/RegistryIdentifierUtilRest';
import * as SampleActions from 'samples/SampleActions';
import * as StorableActions from 'samples/StorableActions';

export function _getTaggedItemIdsFromTable(
  table: TableData,
  computedTable: Array<Array<EvaluatedCell>>
): {bioentityIds: Array<string>; batchIds: Array<string>; storableIds: Array<string>} {
  const columnDefinitions = TableUtil.getColumnDefinitionsFromTable(table);
  const bioentityIds = new Set<string>();
  const batchIds = new Set<string>();
  const storableIds = new Set<string>();
  let existingBioentityColumnIndex = -1;
  let batchColumnIndex = -1;
  let boxColumnIndex = -1;
  let plateColumnIndex = -1;
  if (columnDefinitions != null) {
    existingBioentityColumnIndex = getColumnDefinitionIndexForSample(
      columnDefinitions,
      TableColumnType.EXISTING_BIOENTITY
    );
    batchColumnIndex = getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.BATCH,
      tableColumnDefinitions: columnDefinitions,
      isOptional: true,
    });
    // TODO(BNCH-10526): Add support for storables in other columns besides PLATE and BOX columns
    plateColumnIndex = getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.PLATE,
      tableColumnDefinitions: columnDefinitions,
      isOptional: true,
    });

    boxColumnIndex = getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.BOX,
      tableColumnDefinitions: columnDefinitions,
      isOptional: true,
    });

    for (const row of computedTable) {
      if (existingBioentityColumnIndex !== -1) {
        const bioentityInfo = getBioentityInfoFromTypedValue(row[existingBioentityColumnIndex].value);
        if (bioentityInfo) {
          bioentityIds.add(bioentityInfo.id);
        }
      }
      if (batchColumnIndex !== -1) {
        const batchInfo = getBatchInfoFromTypedValue(row[batchColumnIndex].value);
        if (batchInfo?.id != null) {
          batchIds.add(batchInfo.id);
        }
      }
      if (plateColumnIndex !== -1) {
        const storableInfo = getStorableInfoFromTypedValue(row[plateColumnIndex].value);
        if (storableInfo != null) {
          storableIds.add(storableInfo.id);
        }
      }
      if (boxColumnIndex !== -1) {
        const storableInfo = getStorableInfoFromTypedValue(row[boxColumnIndex].value);
        if (storableInfo != null) {
          storableIds.add(storableInfo.id);
        }
      }
    }
  }
  return {
    bioentityIds: Array.from(bioentityIds),
    batchIds: Array.from(batchIds),
    storableIds: Array.from(storableIds),
  };
}

function updateCellOfColumnType(
  row: Array<Cell>,
  columnType: TableColumnType,
  columnDefinitions: Array<ColumnDefinition | null>,
  updateFn: (cell: Cell) => Cell
): Array<Cell> {
  return row.map((cell, columnIndex) => {
    const columnDefinition = columnDefinitions[columnIndex];
    if (columnDefinition != null && columnDefinition.type === columnType) {
      return updateFn(cell);
    }
    return cell;
  });
}

function updateBioentityNameInRow(
  columnDefinitions: Array<ColumnDefinition | null>,
  bioentitySummary: BioentitySummary | SequenceSummary | null,
  row: Array<Cell>
): Array<Cell> {
  return updateCellOfColumnType(row, TableColumnType.BIOENTITY_NAME, columnDefinitions, (cell) => ({
    text: bioentitySummary == null ? '' : bioentitySummary.name,
  }));
}

function updateBioentitySummaryInRow(
  columnDefinitions: Array<ColumnDefinition | null>,
  bioentitySummary: BioentitySummary | SequenceSummary | null,
  row: Array<Cell>
): Array<Cell> {
  if (bioentitySummary == null) {
    return row;
  }
  // NOTE: This intentionally does not use updateCellOfColumnType because it requires the more nuanced
  // column lookup behavior of getColumnDefinitionIndexForSample
  const existingBioentityColumnDefinitionIndex = getColumnDefinitionIndexForSample(
    columnDefinitions,
    TableColumnType.EXISTING_BIOENTITY
  );
  return row.map((cell, columnIndex) => {
    if (columnIndex === existingBioentityColumnDefinitionIndex) {
      return updateCellWithLiveValue(cell, getTypedValueFromBioentitySummary(bioentitySummary));
    }
    return cell;
  });
}

function updateBasesInRow(
  columnDefinitions: Array<ColumnDefinition | null>,
  bioentitySummary: BioentitySummary | SequenceSummary | null,
  row: Array<Cell>
): Array<Cell> {
  if (bioentitySummary != null) {
    const updateFn = (cell: Cell): Cell => ({
      // eslint-disable-next-line benchlint/no-in-operator
      text: 'bases' in bioentitySummary && bioentitySummary.bases ? bioentitySummary.bases : '',
    });
    // Create a text-only cell for the bases column of either DNA or RNA oligos. This is a short-term solution
    // as this codepath is deprecated.
    const rowWithUpdatedDNAColumn = updateCellOfColumnType(
      row,
      TableColumnType.DNA_BASES,
      columnDefinitions,
      updateFn
    );
    // Update the RNA column separately as updateCellOfColumnType only accepts a single column type.
    return updateCellOfColumnType(
      rowWithUpdatedDNAColumn,
      TableColumnType.RNA_BASES,
      columnDefinitions,
      updateFn
    );
  }
  return row;
}

function updateAliasInRow(
  columnDefinitions: Array<ColumnDefinition | null>,
  bioentitySummary: BioentitySummary | SequenceSummary | null,
  row: Array<Cell>
): Array<Cell> {
  if (bioentitySummary != null) {
    const bioentityAliasInfos: Array<BioentityAliasInfo> =
      RegistryIdentifierUtilRest.getNonRegistryIdentifierAliasInfos(bioentitySummary);
    return updateCellOfColumnType(row, TableColumnType.ALIAS, columnDefinitions, (cell) => {
      if (bioentityAliasInfos.length === 0) {
        return updateCellWithLiveValue(cell, {type: BaseTypedValueType.ARRAY, rawValue: []});
      }
      const typedValue: TypedValue = {
        type: BaseTypedValueType.ARRAY,
        rawValue: bioentityAliasInfos.map(({alias}) => ({
          type: BaseTypedValueType.STRING,
          rawValue: alias,
        })),
      };
      return updateCellWithLiveValue(cell, typedValue);
    });
  }
  return row;
}

function updateBioentityNameAliasAndSummaryAndBasesInRow(
  columnDefinitions: Array<ColumnDefinition | null>,
  bioentitySummary: BioentitySummary | SequenceSummary | null,
  row: Array<Cell>
): Array<Cell> {
  return updateBioentityNameInRow(
    columnDefinitions,
    bioentitySummary,
    updateBioentitySummaryInRow(
      columnDefinitions,
      bioentitySummary,
      updateAliasInRow(
        columnDefinitions,
        bioentitySummary,
        updateBasesInRow(columnDefinitions, bioentitySummary, row)
      )
    )
  );
}

/**
 * @deprecated
 * For the Plate Creation Table
 */
function updatePlateInRow(
  columnDefinitions: Array<ColumnDefinition | null>,
  plateSummary: StorableSummary | null,
  row: Array<Cell>
): Array<Cell> {
  if (plateSummary != null) {
    return updateCellOfColumnType(row, TableColumnType.PLATE, columnDefinitions, (cell) =>
      updateCellWithLiveValue(cell, getTypedValueFromStorableSummaries([plateSummary]))
    );
  }
  return row;
}

function updateBoxInRow(
  columnDefinitions: Array<ColumnDefinition | null>,
  boxSummary: StorableSummary | null,
  row: Array<Cell>
): Array<Cell> {
  if (boxSummary != null) {
    return updateCellOfColumnType(row, TableColumnType.BOX, columnDefinitions, (cell) =>
      updateCellWithLiveValue(cell, getTypedValueFromStorableSummaries([boxSummary]))
    );
  }
  return row;
}

function updateLocationInRow(
  columnDefinitions: Array<ColumnDefinition | null>,
  locationSummary: StorableSummary | null,
  row: Array<Cell>
): Array<Cell> {
  if (locationSummary != null) {
    return updateCellOfColumnType(row, TableColumnType.PARENT_STORABLE, columnDefinitions, (cell) =>
      updateCellWithLiveValue(cell, getTypedValueFromStorableSummaries([locationSummary]))
    );
  }
  return row;
}

/**
 * @deprecated
 * Updates cells related to the BioentitySummary and it's bases in a table after a refresh
 *
 * @param columnDefinitions - the columns contained in the table
 * @param bioentitySummariesById - a map of the updated BioentitySummaries that were refreshed
 * @param cells - the table to be updated
 * @param computedTable - the evaluated table which is needed for the BatchInfo
 */
export function updateBioentityNameAndSummaryAndBasesInTable({
  columnDefinitions,
  bioentitySummariesById,
  cells,
  computedTable,
}: {
  columnDefinitions: Array<ColumnDefinition | null>;
  bioentitySummariesById: {
    [bioentityId: string]: BioentitySummary | SequenceSummary;
  };
  cells: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
}): Cells {
  return withBioentitySummaryPlateBoxAndBatchByRow({
    columnDefinitions,
    bioentitySummariesById,
    cells,
    computedTable,
    batchesById: {},
    storableSummariesById: {},
  }).map(({bioentitySummaryForRow, row}) =>
    updateBioentityNameAliasAndSummaryAndBasesInRow(columnDefinitions, bioentitySummaryForRow, row)
  );
}

export function updatePlateInTable({
  columnDefinitions,
  storableSummariesById,
  cells,
  computedTable,
}: {
  columnDefinitions: Array<ColumnDefinition | null>;
  storableSummariesById: {
    [storableId: string]: StorableSummary;
  };
  cells: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
}): Cells {
  return withBioentitySummaryPlateBoxAndBatchByRow({
    columnDefinitions,
    bioentitySummariesById: {},
    cells,
    computedTable,
    batchesById: {},
    storableSummariesById,
  }).map(({plateForRow, row}) => updatePlateInRow(columnDefinitions, plateForRow, row));
}

export function updateBoxInTable({
  columnDefinitions,
  storableSummariesById,
  cells,
  computedTable,
}: {
  columnDefinitions: Array<ColumnDefinition | null>;
  storableSummariesById: {
    [storableId: string]: StorableSummary;
  };
  cells: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
}): Cells {
  return withBioentitySummaryPlateBoxAndBatchByRow({
    columnDefinitions,
    bioentitySummariesById: {},
    cells,
    computedTable,
    batchesById: {},
    storableSummariesById,
  }).map(({boxForRow, row}) => updateBoxInRow(columnDefinitions, boxForRow, row));
}

export function updateLocationInTable({
  columnDefinitions,
  locationSummariesById,
  storableSummariesById,
  cells,
  computedTable,
}: {
  columnDefinitions: Array<ColumnDefinition | null>;
  locationSummariesById: {
    [storableId: string]: StorableSummary;
  };
  storableSummariesById: {
    [storableId: string]: StorableSummary;
  };
  cells: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
}): Cells {
  return withLocationsByRow({
    columnDefinitions,
    locationSummariesById,
    storableSummariesById,
    cells,
    computedTable,
  }).map(({locationForRow, row}) => updateLocationInRow(columnDefinitions, locationForRow, row));
}

/**
 * @deprecated
 * This function returns an array of objects that contain the bioentity summary, batch, plate, box, and row.
 * The array is indexed as the table's rows.
 */
function withBioentitySummaryPlateBoxAndBatchByRow({
  columnDefinitions,
  bioentitySummariesById,
  batchesById,
  storableSummariesById,
  cells,
  computedTable,
}: {
  columnDefinitions: Array<ColumnDefinition | null>;
  bioentitySummariesById: {
    [bioentityId: string]: BioentitySummary | SequenceSummary;
  };
  batchesById: {
    [batchId: string]: Batch;
  };
  storableSummariesById: {
    [storableId: string]: StorableSummary;
  };
  cells: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
}): Array<{
  bioentitySummaryForRow: BioentitySummary | null;
  batchForRow: Batch | null;
  plateForRow: StorableSummary | null;
  boxForRow: StorableSummary | null;
  row: Array<Cell>;
}> {
  const existingBioentityColumnDefinitionIndex = getColumnDefinitionIndexForSample(
    columnDefinitions,
    TableColumnType.EXISTING_BIOENTITY
  );
  const batchColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.BATCH,
    tableColumnDefinitions: columnDefinitions,
    isOptional: true,
  });
  const plateColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.PLATE,
    tableColumnDefinitions: columnDefinitions,
    isOptional: true,
  });

  const boxColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.BOX,
    tableColumnDefinitions: columnDefinitions,
    isOptional: true,
  });

  return cells.map((row, rowIndex) => {
    const existingBioentityTypedValue =
      existingBioentityColumnDefinitionIndex !== -1
        ? row[existingBioentityColumnDefinitionIndex]?.typedValue
        : null;
    const bioentityInfo =
      existingBioentityTypedValue != null
        ? getBioentityInfoFromTypedValue(existingBioentityTypedValue)
        : null;
    const bioentitySummaryForRow = bioentityInfo != null ? bioentitySummariesById[bioentityInfo.id] : null;

    const batchTypedValue = batchColumnIndex !== -1 ? computedTable[rowIndex][batchColumnIndex]?.value : null;
    const batchInfo = batchTypedValue != null ? getBatchInfoFromTypedValue(batchTypedValue) : null;
    const batchForRow = batchInfo?.id != null ? batchesById[batchInfo.id] : null;

    const plateTypedValue = plateColumnIndex !== -1 ? computedTable[rowIndex][plateColumnIndex]?.value : null;
    const plateInfo = plateTypedValue != null ? getStorableInfoFromTypedValue(plateTypedValue) : null;
    const plateForRow = plateInfo != null ? storableSummariesById[plateInfo.id] : null;

    const boxTypedValue = boxColumnIndex !== -1 ? computedTable[rowIndex][boxColumnIndex]?.value : null;
    const boxInfo = boxTypedValue != null ? getStorableInfoFromTypedValue(boxTypedValue) : null;
    const boxForRow = boxInfo != null ? storableSummariesById[boxInfo.id] : null;

    return {bioentitySummaryForRow, batchForRow, plateForRow, boxForRow, row};
  });
}

function withLocationsByRow({
  columnDefinitions,
  locationSummariesById,
  storableSummariesById,
  cells,
  computedTable,
}: {
  columnDefinitions: Array<ColumnDefinition | null>;
  locationSummariesById: {
    [storableId: string]: StorableSummary;
  };
  storableSummariesById: {
    [storableId: string]: StorableSummary;
  };
  cells: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
}): Array<{
  locationForRow: StorableSummary | null;
  row: Array<Cell>;
}> {
  const plateColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.PLATE,
    tableColumnDefinitions: columnDefinitions,
    isOptional: true,
  });

  return cells.map((row, rowIndex) => {
    const plateTypedValue = plateColumnIndex !== -1 ? computedTable[rowIndex][plateColumnIndex]?.value : null;
    const plateInfo = plateTypedValue != null ? getStorableInfoFromTypedValue(plateTypedValue) : null;
    const plateSummary = plateInfo != null ? storableSummariesById[plateInfo.id] : null;
    const plateParentStorableId = plateSummary?.parentStorableId;
    const locationForRow =
      plateParentStorableId != null ? locationSummariesById[plateParentStorableId] : null;
    return {locationForRow, row};
  });
}

export function _mergeTagsIntoTable({
  cells,
  computedTable,
  columnDefinitions,
  structuredTableColumns,
  bioentitySummariesById,
  batchesById,
  storableSummariesById,
}: {
  cells: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
  columnDefinitions: Array<ColumnDefinition | null>;
  structuredTableColumns: Array<StructuredTableColumn | null>;
  bioentitySummariesById: {[bioentityId: string]: BioentitySummary};
  batchesById: {[batchId: string]: Batch};
  storableSummariesById: {[storableId: string]: StorableSummary};
}): {cells: Cells; lastSyncedUpstreamValuesByRow: Array<Array<LastSyncedUpstreamValue>>} {
  const updatedCells = cells.map((row) => Array(row.length).fill(null));
  const lastSyncedUpstreamValuesByRow: Array<Array<LastSyncedUpstreamValue>> = cells.map((_) => []);
  // Use the same submittedAt time for all of the last synced values
  const submittedAt = new Date().toISOString();

  withBioentitySummaryPlateBoxAndBatchByRow({
    columnDefinitions,
    bioentitySummariesById,
    storableSummariesById,
    cells,
    computedTable,
    batchesById,
  }).forEach(({bioentitySummaryForRow, batchForRow, plateForRow, boxForRow, row}, rowIndex) => {
    const syncedValues: Array<LastSyncedUpstreamValue> = [];

    row.forEach((cell, i) => {
      const columnDefinition = columnDefinitions[i];
      if (!columnDefinition) {
        updatedCells[rowIndex][i] = cell;
        return;
      }

      const isSchemaFieldColumn = columnDefinition.type === TableColumnType.TAG_VALUE;
      if (!isSchemaFieldColumn) {
        updatedCells[rowIndex][i] = cell;
        return;
      }

      if (bioentitySummaryForRow) {
        const bioentityTag = bioentitySummaryForRow.tags.find(
          (tag: Tag) => isSchemaTag(tag) && tag.tagSchemaField.apiId === columnDefinition.schemaField.apiId
        ) as SchemaTag | undefined;
        if (bioentityTag) {
          const typedValue = convertTagToTypedValue(bioentityTag);
          updatedCells[rowIndex][i] = updateCellWithLiveValue(cell, typedValue);
          syncedValues.push({
            columnType: TableColumnType.TAG_VALUE,
            columnGroup: columnDefinition.columnGroup || null,
            fieldId: bioentityTag.tagSchemaField.apiId,
            columnProvenanceUuid: structuredTableColumns[i]?.provenanceUuid ?? null,
            value: typedValue,
            submittedAt,
          });
          return;
        }
      }

      if (batchForRow) {
        const batchTag = batchForRow.tags.find(
          (tag: SchemaTag) => tag.tagSchemaField.apiId === columnDefinition.schemaField.apiId
        );
        if (batchTag) {
          // We don't update the syncedValues here as we don't support Batches for the new Reg Table.
          // Instead, we just update the cell
          updatedCells[rowIndex][i] = updateCellWithLiveValue(cell, convertTagToTypedValue(batchTag));
          return;
        }
      }

      if (boxForRow) {
        const boxTag = boxForRow.tags.find(
          (tag: SchemaTag) => tag.tagSchemaField.apiId === columnDefinition.schemaField.apiId
        );
        if (boxTag) {
          const typedValue = convertTagToTypedValue(boxTag);
          updatedCells[rowIndex][i] = updateCellWithLiveValue(cell, typedValue);
          syncedValues.push({
            columnType: TableColumnType.TAG_VALUE,
            columnGroup: columnDefinition.columnGroup || null,
            fieldId: boxTag.tagSchemaField.apiId,
            columnProvenanceUuid: structuredTableColumns[i]?.provenanceUuid ?? null,
            value: typedValue,
            submittedAt,
          });
          return;
        }
      }

      if (plateForRow) {
        const plateTag = plateForRow.tags.find(
          (tag: SchemaTag) => tag.tagSchemaField.apiId === columnDefinition.schemaField.apiId
        );
        if (plateTag) {
          const typedValue = convertTagToTypedValue(plateTag);
          updatedCells[rowIndex][i] = updateCellWithLiveValue(cell, typedValue);
          syncedValues.push({
            columnType: TableColumnType.TAG_VALUE,
            columnGroup: columnDefinition.columnGroup || null,
            fieldId: plateTag.tagSchemaField.apiId,
            columnProvenanceUuid: structuredTableColumns[i]?.provenanceUuid ?? null,
            value: typedValue,
            submittedAt,
          });
          return;
        }
      }
      updatedCells[rowIndex][i] = cell;
    });
    lastSyncedUpstreamValuesByRow[rowIndex] = syncedValues;
  });
  return {cells: updatedCells, lastSyncedUpstreamValuesByRow};
}

async function _fetchTableData({
  bioentityIds,
  batchIds,
  serverLookupQueries,
}: {
  bioentityIds: Array<string>;
  batchIds: Array<string>;
  serverLookupQueries: Array<LookupSpecifier>;
}): Promise<RefreshTableDataDAO.RefreshTableDataResponse> {
  if (bioentityIds.length + batchIds.length + serverLookupQueries.length === 0) {
    return Promise.resolve({
      bioentitySummariesById: {},
      batchesById: {},
      entrySummariesById: {},
      lookupFieldData: [],
      lookupFieldErrors: [],
    });
  }
  // TODO(BNCH-10535): Remove batches from the refreshTableData endpoint
  // TODO(BNCH-10536): Remove bioentities from the refreshTableData endpoint
  const response = await RefreshTableDataDAO.refreshTableData(bioentityIds, batchIds, serverLookupQueries);
  const {bioentitySummariesById, batchesById, entrySummariesById} = response;
  getStore().dispatch(ModelActions.filesFetchCompleted(bioentitySummariesById));
  getStore().dispatch(ModelActions.entriesFetchCompleted(entrySummariesById));
  SampleActions.handleBatchesLoaded(Object.values(batchesById));
  return response;
}

async function _fetchStorables({
  storableIds,
}: {
  storableIds: Array<string>;
}): Promise<{[storableId: string]: StorableSummary}> {
  if (storableIds.length === 0) {
    return Promise.resolve({});
  }
  const storableSummaries = await StorableActions.bulkFetchSummaries(storableIds);
  // eslint-disable-next-line benchlint/no-spread-reduce
  const storableSummariesById = storableSummaries.reduce(
    (acc, storableSummary) => ({...acc, [storableSummary.id]: storableSummary}),
    {} as {[storableId: string]: StorableSummary}
  );
  return storableSummariesById;
}

/**
 * @deprecated
 */
export async function handleRefreshTableData({
  computedTable,
  table,
  onUpdateTable,
  fetchColumnUpdates,
}: {
  computedTable: Array<Array<EvaluatedCell>>;
  table: TableData;
  onUpdateTable: ({
    updatedCells,
    columnUpdates,
    lastSyncedUpstreamValuesByRow,
  }: {
    updatedCells: Cells;
    columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
    lastSyncedUpstreamValuesByRow: Array<Array<LastSyncedUpstreamValue>>;
  }) => void;
  fetchColumnUpdates: () => Promise<{
    [columnId: number]: {} | SchemaFieldColumnUpdate;
  }>;
}): Promise<void> {
  const [height, width] = TableUtil.getDimensions(table);
  const columnDefinitions = TableUtil.getColumnDefinitionsFromTable(table);

  let serverLookupQueries = [];
  try {
    serverLookupQueries = getServerLookupQueries({
      minI: 0,
      maxI: height - 1,
      minJ: 0,
      maxJ: width - 1,
      table,
      computedTable,
      columnDefinitions,
    });
  } catch (e) {
    if (e instanceof ExcessUtil.FormulaParseError) {
      notifications.error(e.message);
      return;
    }
    throw e;
  }

  let updatedCells = table.table;
  let lastSyncedUpstreamValuesByRow: Array<Array<LastSyncedUpstreamValue>> = Array(updatedCells.length).fill(
    []
  );

  const {bioentityIds, batchIds, storableIds} = _getTaggedItemIdsFromTable(table, computedTable);

  const [
    {bioentitySummariesById, batchesById, lookupFieldData, lookupFieldErrors},
    columnUpdates,
    storableSummariesById,
  ] = await Promise.all([
    _fetchTableData({bioentityIds, batchIds, serverLookupQueries}),
    fetchColumnUpdates(),
    _fetchStorables({storableIds}),
  ]);

  if (lookupFieldErrors.length > 0) {
    for (const error of lookupFieldErrors) {
      notifications.error(error);
    }
  }
  if (lookupFieldData.length) {
    updatedCells = mergeServerLookupResultsIntoCells({
      minI: 0,
      maxI: height - 1,
      minJ: 0,
      maxJ: width - 1,
      table,
      serverLookupQueries,
      serverLookupResults: lookupFieldData,
      computedTable,
      columnDefinitions,
    });
  }

  if (columnDefinitions != null && (bioentityIds.length || batchIds.length || storableIds.length)) {
    const bioentitySummaryPlateBoxAndBatchByRow = withBioentitySummaryPlateBoxAndBatchByRow({
      columnDefinitions,
      bioentitySummariesById: {},
      cells: updatedCells,
      computedTable,
      batchesById: {},
      storableSummariesById,
    });

    const allSummariesInTable: Array<StorableSummary> = [];
    for (const {boxForRow, plateForRow} of bioentitySummaryPlateBoxAndBatchByRow) {
      if (boxForRow != null) {
        allSummariesInTable.push(boxForRow);
      }
      if (plateForRow != null) {
        allSummariesInTable.push(plateForRow);
      }
    }

    // Fetch parent storables for plates and boxes
    const locationIdsInTable: Array<string> = allSummariesInTable
      .map((summary) => summary?.parentStorableId)
      .filter((parentStorableId) => parentStorableId != null);
    const locationSummariesById = await _fetchStorables({storableIds: locationIdsInTable});

    updatedCells = updateLocationInTable({
      columnDefinitions,
      locationSummariesById,
      storableSummariesById,
      cells: updatedCells,
      computedTable,
    });

    updatedCells = updateBioentityNameAndSummaryAndBasesInTable({
      columnDefinitions,
      bioentitySummariesById,
      cells: updatedCells,
      computedTable,
    });

    updatedCells = updatePlateInTable({
      columnDefinitions,
      storableSummariesById,
      cells: updatedCells,
      computedTable,
    });

    updatedCells = updateBoxInTable({
      columnDefinitions,
      storableSummariesById,
      cells: updatedCells,
      computedTable,
    });

    const updates = _mergeTagsIntoTable({
      cells: updatedCells,
      computedTable,
      columnDefinitions,
      structuredTableColumns: TableUtil.getStructuredTableColumnsFromTable(table),
      bioentitySummariesById,
      batchesById,
      storableSummariesById,
    });
    updatedCells = updates.cells;
    lastSyncedUpstreamValuesByRow = updates.lastSyncedUpstreamValuesByRow;
  }
  onUpdateTable({updatedCells, columnUpdates, lastSyncedUpstreamValuesByRow});
}

/**
 * @deprecated
 */
export async function handleRefreshDataTableData({
  entryOrTemplateId,
  table,
  computedTable,
  onUpdateTable,
}: {
  entryOrTemplateId: string;
  table: TableData;
  computedTable: Array<Array<EvaluatedCell>>;
  onUpdateTable: ({
    updatedCells,
    columnUpdates,
  }: {
    updatedCells: Cells;
    columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
  }) => void;
}): Promise<void> {
  const [height, width] = TableUtil.getDimensions(table);
  const columnDefinitions = TableUtil.getColumnDefinitionsFromTable(table);

  let serverLookupQueries: ServerLookupAggregatedQuery = [];
  if (table.rowData != null && table.rowData.length > 0) {
    try {
      serverLookupQueries = getServerLookupQueries({
        minI: 0,
        maxI: height - 1,
        minJ: 0,
        maxJ: width - 1,
        table,
        computedTable,
        columnDefinitions,
      });
    } catch (e) {
      if (e instanceof ExcessUtil.FormulaParseError) {
        notifications.error(e.message);
        return;
      }
      throw e;
    }
  }

  let columnUpdates: {[columnId: string]: SchemaFieldColumnUpdate | {}};
  let updatedCells = table.table;

  if (serverLookupQueries.length === 0) {
    // Columns is the only thing we need to refresh
    columnUpdates = (await RefreshTableDataDAO.getDataTableColumnUpdates(entryOrTemplateId, table.name))
      .columnUpdates;
  } else {
    const [lookupFieldRefreshData, columnUpdateResponse] = await Promise.all([
      SearchDAO.lookupFieldValues(serverLookupQueries),
      RefreshTableDataDAO.getDataTableColumnUpdates(entryOrTemplateId, table.name),
    ]);

    columnUpdates = columnUpdateResponse.columnUpdates;

    if (lookupFieldRefreshData.errors.length > 0) {
      for (const error of lookupFieldRefreshData.errors) {
        notifications.error(error);
      }
    }

    if (lookupFieldRefreshData.data != null) {
      assert(
        lookupFieldRefreshData.data.length === serverLookupQueries.length,
        'The number of results does not match the number of lookup specifiers'
      );

      updatedCells = mergeServerLookupResultsIntoCells({
        minI: 0,
        maxI: height - 1,
        minJ: 0,
        maxJ: width - 1,
        table,
        serverLookupQueries,
        serverLookupResults: lookupFieldRefreshData.data,
        computedTable,
        columnDefinitions,
      });
    }
  }

  onUpdateTable({updatedCells, columnUpdates});
}

/**
 * @deprecated
 */
export async function handleRefreshTableColumns({
  entryOrTemplateId,
  provenanceUuid,
  onUpdateTable,
}: {
  entryOrTemplateId: string;
  provenanceUuid: string;
  onUpdateTable: ({
    columnUpdates,
  }: {
    columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
  }) => void;
}): Promise<void> {
  const columnUpdates = (await RefreshTableDataDAO.getColumnUpdates(entryOrTemplateId, provenanceUuid))
    .columnUpdates;

  onUpdateTable({columnUpdates});
}

/**
 * @deprecated
 */
export async function handleRefreshDataTableColumns({
  entryOrTemplateId,
  tableName,
  onUpdateTable,
}: {
  entryOrTemplateId: string;
  tableName: string;
  onUpdateTable: ({
    columnUpdates,
  }: {
    columnUpdates: {[columnId: number]: SchemaFieldColumnUpdate | {}};
  }) => void;
}): Promise<void> {
  const columnUpdates = (await RefreshTableDataDAO.getDataTableColumnUpdates(entryOrTemplateId, tableName))
    .columnUpdates;

  onUpdateTable({columnUpdates});
}

function getUpdatedConstraint(
  columnContraint: ColumnConstraint,
  columnUpdates: {
    [columnId: number]: SchemaFieldColumnUpdate | {};
  },
  getIdFromCid: (cid: string) => string | number | null | undefined
): ColumnConstraint {
  if (columnContraint.structuredTableColumn == null) {
    return columnContraint;
  }
  const dbId = getIdFromCid(columnContraint.structuredTableColumn.cid);
  const columnUpdate = columnUpdates[dbId as number];
  if (columnUpdate == null) {
    // Possibly the column has been just added or deleted from a different browser, so we don't have an update for it
    return columnContraint;
  }
  return {
    ...columnContraint,
    structuredTableColumn: {
      ...columnContraint.structuredTableColumn,
      ...columnUpdate,
    },
  };
}

export function mergeColumnUpdatesIntoColumnConstraints(
  table: TableData,
  columnUpdates: {
    [columnId: number]: SchemaFieldColumnUpdate | {};
  },
  getIdFromCid: (cid: string) => string | number | null | undefined
): Array<ColumnConstraint | null> {
  return table.columnConstraints.map((constraint) =>
    constraint == null ? null : getUpdatedConstraint(constraint, columnUpdates, getIdFromCid)
  );
}
